import type {IndexingDim} from "#template/geometry-processing/geometry-graph"
import {AttributeRef, ValueRef, valueRef, attributeRef, indexingDim} from "#template/geometry-processing/geometry-graph"
export type {IndexingDim} from "#template/geometry-processing/geometry-graph"
export {indexingDim} from "#template/geometry-processing/geometry-graph"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {DataNodes} from "@cm/render-nodes"
import type {TemplateNode} from "#template/template-node/template-node"
import {z} from "zod/v4-mini"

// @cm/render-nodes still uses zod v3, so the v3 `EvaluationResourcesSchema` isn't directly
// composable into a zod-mini object. We delegate to its `safeParse` inside a `z.custom` and carry
// the precise TS type so consumers get full type information.
const evaluationResources = z.custom<DataNodes.EvaluationResources>((x) => DataNodes.EvaluationResourcesSchema.safeParse(x).success)

export type GeometryValueRefData = {
    renderGraphRef: ValueRef
    displayGraphRef: ValueRef
    displayGraphResources: DataNodes.EvaluationResources
}

export const geometryValueRefData = z.object({
    renderGraphRef: valueRef,
    displayGraphRef: valueRef,
    displayGraphResources: evaluationResources,
})

export type GeometryAttributeRefData = {
    renderGraphRef: AttributeRef
    displayGraphRef: AttributeRef
    displayGraphResources: DataNodes.EvaluationResources
}

export const geometryAttributeRefData = z.object({
    renderGraphRef: attributeRef,
    displayGraphRef: attributeRef,
    displayGraphResources: evaluationResources,
})

export type GeometryAttributeRefDataSet = {[key: string]: GeometryAttributeRefData | GeometryAttributeRefDataSet}

export type GeometryValueData = {
    type: "value"
    ref: GeometryValueRefData
}
export const geometryValueData = z.object({
    type: z.literal("value"),
    ref: geometryValueRefData,
})

export type GeometryAttributeData<T extends IndexingDim = IndexingDim> = {
    type: "attribute"
    dim: T
    ref: GeometryAttributeRefData
}

export type PointGeometryAttributeData = GeometryAttributeData<0>
export type CurveGeometryAttributeData = GeometryAttributeData<1>
export type MeshGeometryAttributeData = GeometryAttributeData<2>

export const geometryAttributeData = z.object({
    type: z.literal("attribute"),
    dim: indexingDim,
    ref: geometryAttributeRefData,
})
export const pointGeometryAttributeData = z.custom<PointGeometryAttributeData>(
    (x) => geometryAttributeData.safeParse(x).success && isPointGeometryAttributeData(x as GeometryAttributeData),
)
export const curveGeometryAttributeData = z.custom<CurveGeometryAttributeData>(
    (x) => geometryAttributeData.safeParse(x).success && isCurveGeometryAttributeData(x as GeometryAttributeData),
)
export const meshGeometryAttributeData = z.custom<MeshGeometryAttributeData>(
    (x) => geometryAttributeData.safeParse(x).success && isMeshGeometryAttributeData(x as GeometryAttributeData),
)

export interface BaseGeometryAttributeSetData<D, T extends IndexingDim, K extends string = string> {
    type: "attributeSet"
    dim: T
    data: {[key in K]: D}
}

export type GeometryAttributeSetData<T extends IndexingDim = IndexingDim, K extends string = string> = BaseGeometryAttributeSetData<
    GeometryAttributeData<T> | GeometryAttributeSetData<T, K>,
    T,
    K
>

export type PointGeometryAttributeSetData<K extends string = string> = GeometryAttributeSetData<0, K>
export type CurveGeometryAttributeSetData<K extends string = string> = GeometryAttributeSetData<1, K>
export type MeshGeometryAttributeSetData<K extends string = string> = GeometryAttributeSetData<2, K>

export const geometryAttributeSetData: z.ZodMiniType<GeometryAttributeSetData, GeometryAttributeSetData> = z.lazy(() =>
    z.object({
        type: z.literal("attributeSet"),
        dim: indexingDim,
        data: z.record(z.string(), z.union([geometryAttributeData, geometryAttributeSetData])),
    }),
)
export const pointGeometryAttributeSetData = z.custom<PointGeometryAttributeSetData>(
    (x) => geometryAttributeSetData.safeParse(x).success && isPointGeometryAttributeSetData(x as GeometryAttributeSetData),
)
export const curveGeometryAttributeSetData = z.custom<CurveGeometryAttributeSetData>(
    (x) => geometryAttributeSetData.safeParse(x).success && isCurveGeometryAttributeSetData(x as GeometryAttributeSetData),
)
export const meshGeometryAttributeSetData = z.custom<MeshGeometryAttributeSetData>(
    (x) => geometryAttributeSetData.safeParse(x).success && isMeshGeometryAttributeSetData(x as GeometryAttributeSetData),
)

export function createGeometryValueRefData(
    renderGraphRef: ValueRef,
    displayGraphRef: ValueRef,
    displayGraphResources: DataNodes.EvaluationResources,
): GeometryValueRefData {
    return {renderGraphRef, displayGraphRef, displayGraphResources}
}

export function createGeometryAttributeRefData(
    renderGraphRef: AttributeRef,
    displayGraphRef: AttributeRef,
    displayGraphResources: DataNodes.EvaluationResources,
): GeometryAttributeRefData {
    return {renderGraphRef, displayGraphRef, displayGraphResources}
}

export function createGeometryValueData(ref: GeometryValueRefData): GeometryValueData {
    return {type: "value", ref}
}

export function createGeometryAttributeData<T extends IndexingDim>(ref: GeometryAttributeRefData, dim: T): GeometryAttributeData<T> {
    return {type: "attribute", ref, dim}
}

export function createGeometryAttributeSetData<T extends IndexingDim, K extends string = string>(
    data: {[key in K]: GeometryAttributeData<T> | GeometryAttributeSetData<T>},
    dim: T,
): GeometryAttributeSetData<T, K> {
    return {type: "attributeSet", data, dim}
}

export function createPointGeometryAttributeData(ref: GeometryAttributeRefData): PointGeometryAttributeData {
    return createGeometryAttributeData(ref, 0)
}

export function createCurveGeometryAttributeData(ref: GeometryAttributeRefData): CurveGeometryAttributeData {
    return createGeometryAttributeData(ref, 1)
}

export function createMeshGeometryAttributeData(ref: GeometryAttributeRefData): MeshGeometryAttributeData {
    return createGeometryAttributeData(ref, 2)
}

export function createPointGeometryAttributeSetData<K extends string = string>(data: {
    [key in K]: PointGeometryAttributeData | PointGeometryAttributeSetData
}): PointGeometryAttributeSetData {
    return createGeometryAttributeSetData(data, 0)
}
export function createCurveGeometryAttributeSetData<K extends string = string>(data: {
    [key in K]: CurveGeometryAttributeData | CurveGeometryAttributeSetData
}): CurveGeometryAttributeSetData<K> {
    return createGeometryAttributeSetData(data, 1)
}
export function createMeshGeometryAttributeSetData<K extends string = string>(data: {
    [key in K]: MeshGeometryAttributeData | MeshGeometryAttributeSetData
}): MeshGeometryAttributeSetData {
    return createGeometryAttributeSetData(data, 2)
}

export function isGeometryValueRefData(data: GeometryValueRefData | GeometryAttributeRefData | GeometryAttributeRefDataSet): data is GeometryValueRefData {
    return "renderGraphRef" in data && "displayGraphRef" in data && data.renderGraphRef instanceof ValueRef && data.displayGraphRef instanceof ValueRef
}

export function isGeometryAttributeRefData(
    data: GeometryValueRefData | GeometryAttributeRefData | GeometryAttributeRefDataSet,
): data is GeometryAttributeRefData {
    return "displayGraphRef" in data && "renderGraphRef" in data && data.displayGraphRef instanceof AttributeRef && data.renderGraphRef instanceof AttributeRef
}

export function isGeometryAttributeRefDataSet(
    node: GeometryValueRefData | GeometryAttributeRefData | GeometryAttributeRefDataSet,
): node is GeometryAttributeRefDataSet {
    return !isGeometryAttributeRefData(node) && !isGeometryValueRefData(node)
}

export function isGeometryAttributeData(data: GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>): data is GeometryAttributeData {
    return data.type === "attribute"
}

export function isGeometryAttributeSetData(data: GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>): data is GeometryAttributeSetData {
    return data.type === "attributeSet"
}

export function isPointGeometryAttributeData(data: GeometryAttributeData<IndexingDim>): data is PointGeometryAttributeData {
    return data.dim === 0 && data.type === "attribute"
}
export function isCurveGeometryAttributeData(data: GeometryAttributeData<IndexingDim>): data is CurveGeometryAttributeData {
    return data.dim === 1 && data.type === "attribute"
}
export function isMeshGeometryAttributeData(data: GeometryAttributeData<IndexingDim>): data is MeshGeometryAttributeData {
    return data.dim === 2 && data.type === "attribute"
}

export function isPointGeometryAttributeSetData(data: GeometryAttributeSetData<IndexingDim>): data is PointGeometryAttributeSetData {
    return data.dim === 0 && data.type === "attributeSet"
}
export function isCurveGeometryAttributeSetData(data: GeometryAttributeSetData<IndexingDim>): data is CurveGeometryAttributeSetData {
    return data.dim === 1 && data.type === "attributeSet"
}
export function isMeshGeometryAttributeSetData(data: GeometryAttributeSetData<IndexingDim>): data is MeshGeometryAttributeSetData {
    return data.dim === 2 && data.type === "attributeSet"
}

////////////////////////////////////////////////////////////////////////////////

const standardCurveAttributeNames = ["points", "normals", "tangents", "segments", "scales", "sectionIds"] as const
type StandardCurveAttributeNames = (typeof standardCurveAttributeNames)[number]

export type StandardCurveGeometryAttributeSetData = BaseGeometryAttributeSetData<CurveGeometryAttributeData, 1, StandardCurveAttributeNames>

export function createStandardCurveGeometryAttributeSetData(data: {
    [key in StandardCurveAttributeNames]: CurveGeometryAttributeData
}): StandardCurveGeometryAttributeSetData {
    return {
        type: "attributeSet",
        dim: 1,
        data,
    }
}

export function isStandardCurveAttributeSetData(
    curveAttributeSetData: CurveGeometryAttributeSetData,
): curveAttributeSetData is StandardCurveGeometryAttributeSetData {
    const checkExpectedValueTypeForStandardAttribute = (attributeData: CurveGeometryAttributeData, attributeName: StandardCurveAttributeNames) => {
        const expectedValueType = (() => {
            switch (attributeName) {
                case "points":
                    return "float3"
                case "normals":
                    return "float3"
                case "tangents":
                    return "float3"
                case "segments":
                    return "float"
                case "scales":
                    return "float"
                case "sectionIds":
                    return "uint32"
                default:
                    throw new Error(`Unknown standard curve attribute name: ${attributeName}`)
            }
        })()

        return attributeData.ref.displayGraphRef._type === expectedValueType && attributeData.ref.renderGraphRef._type === expectedValueType
    }

    return standardCurveAttributeNames.every(
        (key) =>
            isGeometryAttributeData(curveAttributeSetData.data[key]) &&
            isCurveGeometryAttributeData(curveAttributeSetData.data[key]) &&
            checkExpectedValueTypeForStandardAttribute(curveAttributeSetData.data[key], key),
    )
}

////////////////////////////////////////////////////////////////////////////////

const standardMeshAttributeNames = ["position", "normal", "materialID", "uvs"] as const
type StandardMeshAttributeNames = (typeof standardMeshAttributeNames)[number]

export type StandardMeshGeometryAttributeSetData = {
    type: "attributeSet"
    dim: 2
    data: {[key in Exclude<StandardMeshAttributeNames, "uvs">]: MeshGeometryAttributeData} & {uvs: MeshGeometryAttributeSetData}
}

export function createStandardMeshGeometryAttributeSetData(
    data: {
        [key in StandardMeshAttributeNames]: MeshGeometryAttributeData
    } & {uvs: {[key: string]: MeshGeometryAttributeData}},
): StandardMeshGeometryAttributeSetData {
    return {
        type: "attributeSet",
        dim: 2,
        data: {
            ...data,
            uvs: createMeshGeometryAttributeSetData(data.uvs),
        },
    }
}

export function isStandardMeshAttributeSetData(
    meshAttributeSetData: MeshGeometryAttributeSetData,
): meshAttributeSetData is StandardMeshGeometryAttributeSetData {
    const checkExpectedValueTypeForStandardAttribute = (attributeData: MeshGeometryAttributeData, attributeName: StandardMeshAttributeNames) => {
        const expectedValueType = (() => {
            switch (attributeName) {
                case "position":
                    return "float3"
                case "normal":
                    return "float3"
                case "materialID":
                    return "uint32"
                case "uvs":
                    return "float2"
                default:
                    throw new Error(`Unknown standard mesh attribute name: ${attributeName}`)
            }
        })()

        return attributeData.ref.displayGraphRef._type === expectedValueType && attributeData.ref.renderGraphRef._type === expectedValueType
    }

    return standardMeshAttributeNames.every(
        (key) =>
            (isGeometryAttributeData(meshAttributeSetData.data[key]) &&
                isMeshGeometryAttributeData(meshAttributeSetData.data[key]) &&
                checkExpectedValueTypeForStandardAttribute(meshAttributeSetData.data[key], key)) ||
            (key === "uvs" &&
                isGeometryAttributeSetData(meshAttributeSetData.data[key]) &&
                isMeshGeometryAttributeSetData(meshAttributeSetData.data[key]) &&
                Object.values(meshAttributeSetData.data[key].data).every(
                    (value) => isGeometryAttributeData(value) && isMeshGeometryAttributeData(value) && checkExpectedValueTypeForStandardAttribute(value, "uvs"),
                )),
    )
}

////////////////////////////////////////////////////////////////////////////////

export type EvaluableGeometryValueLikeNode<P extends {} = {}> = EvaluableTemplateNode<GeometryValueData | null> & TemplateNode<P>
export type EvaluableGeometryAttributeLikeNode<T extends IndexingDim, P extends {} = {}> = EvaluableTemplateNode<GeometryAttributeData<T> | null> &
    TemplateNode<P>
export type EvaluableGeometryAttributeSetLikeNode<T extends IndexingDim, P extends {} = {}> = EvaluableTemplateNode<GeometryAttributeSetData<T> | null> &
    TemplateNode<P>
